import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Alert, CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";

// Редактор правил бронювання САЛОНУ — лише власник. Префіл — поточні правила
// (або вбудований дефолт, поки не редагували).
export default function BookingTermsEditor() {
  const { t } = useTranslation();
  const { activeWorkspaceId, workspaceRole } = useAuth();
  const isOwner = workspaceRole === "owner";

  const [text, setText] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const d = await api.getWorkspaceTerms(activeWorkspaceId);
      setText(d.text || "");
      setIsDefault(!!d.isDefault);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [activeWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setErr("");
    setMsg("");
    if (!text.trim()) {
      setErr(t("terms.editorEmpty"));
      return;
    }
    setSaving(true);
    try {
      await api.updateWorkspaceTerms(activeWorkspaceId, text);
      setIsDefault(false);
      setMsg(t("terms.editorSaved"));
    } catch (e) {
      setErr(e.message || t("terms.editorSaveError"));
    }
    setSaving(false);
  };

  if (!activeWorkspaceId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{t("terms.noWorkspace")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, maxWidth: 820 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("terms.editorTitle")}
      </Typography>

      {!isOwner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("terms.ownerOnly")}
        </Alert>
      )}

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("terms.editorHint")}
          {isDefault ? ` ${t("terms.editorDefaultNote")}` : ""}
        </Typography>
        {msg && (
          <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMsg("")}>
            {msg}
          </Alert>
        )}
        {err && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr("")}>
            {err}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TextField
              multiline
              minRows={12}
              maxRows={28}
              fullWidth
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={!isOwner}
              placeholder={t("terms.editorPlaceholder")}
              sx={{ "& textarea": { fontFamily: "inherit", fontSize: 14, lineHeight: 1.5 } }}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={save} disabled={!isOwner || saving}>
                {saving ? t("common.saving") : t("common.save")}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
